"use client";
import { useChatbotsList } from "./useChatbotsList";
import { ChatbotsHeader } from "./ChatbotsHeader/ChatbotsHeader";
import { ChatbotsTable } from "./ChatbotsTable";
import { ChatbotsTrialBanner } from "./ChatbotsTrialBanner";
import { ChatbotsUpsell } from "./ChatbotsUpsell/ChatbotsUpsell";
import {
  AssetLoadErrorAlert,
  AssetsSparkSurface,
  BulkActionsWrapper,
  PageSizeProvider,
  Pagination,
  usePageSize,
} from "@zapier/shared";
import {
  BetaDataTableProvider as TableProvider,
  SortDescriptor,
} from "@zapier/design-system";
import { AssetTypes } from "@zapier/utils";

function ChatbotsWithPageSize() {
  const { size: pageSize } = usePageSize();
  const {
    chatbots,
    loadError,
    isLoading,
    isFetching,
    page,
    totalPages,
    totalResults,
    sortChange,
    order,
  } = useChatbotsList({ pageSize });

  const sortDescriptor: SortDescriptor = {
    column: "updatedAt",
    direction: "descending",
  };

  if (order) {
    sortDescriptor.column = order.replace("-", "");
    sortDescriptor.direction = order[0] === "-" ? "descending" : "ascending";
  }

  const hasChatbots = !isLoading && (chatbots?.length ?? 0) > 0;

  return (
    <BulkActionsWrapper
      assetType={AssetTypes.Chatbot}
      assetsForCopyToAnotherAccount={chatbots || []}
    >
      {({ bulkSelectActions, onCopyToAnotherAccount }) => (
        <>
          <ChatbotsHeader showFilters={hasChatbots} />
          {(isLoading || isFetching || (chatbots?.length ?? 0) > 0) && (
            <AssetsSparkSurface
              surfaceId={process.env.NEXT_PUBLIC_SPARK_SURFACE_ID_CHATBOTS}
            />
          )}
          <ChatbotsTrialBanner />
          <ChatbotsUpsell />
          <AssetLoadErrorAlert
            error={loadError}
            assetTypes={[AssetTypes.Chatbot]}
          />
          {(!loadError || chatbots.length > 0) && (
            <ChatbotsTable
              bulkSelectActions={bulkSelectActions}
              chatbots={chatbots || []}
              isLoading={isLoading || isFetching}
              onSortChange={sortChange}
              sortDescriptor={sortDescriptor}
              onCopyToAnotherAccount={onCopyToAnotherAccount}
            />
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalResults}
            isLoading={isLoading}
          />
        </>
      )}
    </BulkActionsWrapper>
  );
}

export function Chatbots() {
  return (
    <PageSizeProvider>
      <TableProvider>
        <ChatbotsWithPageSize />
      </TableProvider>
    </PageSizeProvider>
  );
}
